import type { RefObject } from 'react';
import { useEffect } from 'react';

type UseTimingTrapOptions = {
  videoRef: RefObject<HTMLVideoElement>;
  duration: number;
  startTime: number;
  endTime: number;
  loop: boolean;
  onPause: () => void;
};

type IsDefaultTimingOptions = {
  startTime: number;
  endTime: number;
  duration: number;
};

const isDefaultTiming = ({
  startTime,
  endTime,
  duration,
}: IsDefaultTimingOptions) => {
  return startTime === 0 && duration === endTime;
};

export const useTimingTrap = ({
  videoRef,
  duration,
  startTime,
  endTime,
  loop,
  onPause,
}: UseTimingTrapOptions) => {
  useEffect(() => {
    const video = videoRef.current;
    if (!video || isDefaultTiming({ startTime, endTime, duration })) {
      return;
    }

    const handleTimeUpdate = () => {
      if (video.currentTime < startTime) {
        video.currentTime = startTime;
        return;
      }

      if (video.currentTime > endTime) {
        if (loop) {
          video.currentTime = startTime;
          return;
        } else {
          video.currentTime = endTime;
          return onPause();
        }
      }
    };

    const handlePlay = async () => {
      if (loop) {
        return;
      }

      if (video.currentTime >= endTime) {
        video.currentTime = startTime;
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [videoRef, duration, startTime, endTime, loop, onPause]);
};
